import { Spin } from 'antd';
import { Link } from 'react-router-dom';
import { ItemImage, OrganisationAvatar } from '../../../components';
import { BoldText, MuteText } from '../../../elements';
import { ProductThumb, RowActionsCell } from '../list/inventoryListStyled';
import { ProductTitleEllipsis } from '../list/ProductTitleEllipsis';
import {
  ReplenishmentLotLocationTags,
  ReplenishmentProductLocationTags,
} from './ReplenishmentLocationTags';
import {
  ReplenishmentLotRowActions,
  ReplenishmentProductRowActions,
} from './ReplenishmentRowActions';
import type {
  ReplenishmentColumnKey,
  ReplenishmentItem,
  ReplenishmentLotRow,
} from './replenishmentListTypes';

export type ReplenishmentListCellContext = {
  isIgnoredList: boolean;
  actionLoading: Record<string, boolean>;
  openPopup: (
    item: ReplenishmentItem & { lotIndex: number | string },
    inventoryId?: string,
  ) => void;
  ignoreItem: (item: ReplenishmentItem) => void;
  activateItem: (item: ReplenishmentItem) => void;
};

export function renderReplenishmentProductCell(
  key: ReplenishmentColumnKey,
  item: ReplenishmentItem,
  ctx: ReplenishmentListCellContext,
) {
  const itemId = item._id;
  const isRowLoading = itemId ? ctx.actionLoading[itemId] : false;

  switch (key) {
    case 'description':
      return (
        <div className="d-flex align-items-start" style={{ minWidth: 0 }}>
          <ProductThumb>
            <ItemImage
              imageKey="p_images"
              displayImageKey="display_image"
              item={item.productId}
              size="mini"
              noMargin
            />
          </ProductThumb>
          <div className="ml-4" style={{ minWidth: 0, flex: 1 }}>
            <ProductTitleEllipsis title={item.productId?.p_title || 'Unknown Product'} />
            <div className="d-flex">
              <MuteText className="mr-2">SKU</MuteText>
              {item.productId?.p_sku ? (
                <BoldText>{item.productId?.p_sku}</BoldText>
              ) : (
                <MuteText>N/A</MuteText>
              )}
            </div>
          </div>
        </div>
      );
    case 'organization':
      return (
        <div className="d-flex mt-2">
          <OrganisationAvatar
            name={item?.customerOrganizationId?.company_name}
            image={item?.customerOrganizationId?.logo?.url}
            mini
          />
          &nbsp;{item?.customerOrganizationId?.company_name || 'Unknown Org'}
        </div>
      );
    case 'location':
      return <ReplenishmentProductLocationTags locations={item.locations} />;
    case 'quantity_remaining':
      return (
        <span>
          {item.currentQuantity} <MuteText>Units</MuteText>
        </span>
      );
    case 'actions':
      if (isRowLoading) {
        return <Spin size="small" />;
      }
      return (
        <RowActionsCell>
          <ReplenishmentProductRowActions
            isIgnoredList={ctx.isIgnoredList}
            onEditQuantity={() => ctx.openPopup({ ...item, lotIndex: -1 })}
            onIgnore={() => ctx.ignoreItem(item)}
            onActivate={() => ctx.activateItem(item)}
          />
        </RowActionsCell>
      );
    default:
      return null;
  }
}

export function renderReplenishmentLotCell(
  key: ReplenishmentColumnKey,
  row: ReplenishmentLotRow,
  ctx: ReplenishmentListCellContext,
) {
  const { item, lot } = row;

  switch (key) {
    case 'description':
      return (
        <div>
          <MuteText>Lot #</MuteText>
          <Link
            to={`/app/inventory/item/${lot._id}`}
            className="ml-2"
            onClick={e => e.stopPropagation()}
          >
            {lot.lotNumber || 'N/A'}
          </Link>
        </div>
      );
    case 'organization':
      return (
        <span>
          <MuteText>Alias:</MuteText> {lot?.cartonAttributes?.alias || 'N/A'}
        </span>
      );
    case 'location':
      return <ReplenishmentLotLocationTags location={lot.location} />;
    case 'quantity_remaining':
      return (
        <span>
          {lot.quantity?.available} <MuteText>Units</MuteText>
        </span>
      );
    case 'actions':
      return (
        <RowActionsCell>
          <ReplenishmentLotRowActions
            onEditQuantity={() =>
              ctx.openPopup({ ...item, lotIndex: row.lotIndex }, lot._id)
            }
          />
        </RowActionsCell>
      );
    default:
      return null;
  }
}
